import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CustomLink, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Badge } from '@ygtechnologies/rubus-ui';
import { createDataTable } from '@/components/DataTable';
import { EmailContentListItem } from '@/services/emailTemplates';
import { SortDirection } from '@/types';
import { languageLabel, useScopeInUrl } from './shared';

const DataTable = createDataTable<EmailContentListItem>();

interface EmailContentsTableProps {
    /** Fetched by the page (scoped to the bar's selection), so the bar and the table share one request. */
    data: EmailContentListItem[] | null;
    isLoading: boolean;
}

/** How the template stands at the chosen scope; lower sorts first. */
function statusRank(row: EmailContentListItem): number {
    if (row.scopeOverrideLanguages?.length) {
        return 0;
    }
    return row.profileOverrideLanguages?.length ? 1 : 2;
}

function renderStatus(row: EmailContentListItem) {
    if (row.scopeOverrideLanguages?.length) {
        return (
            <Badge variant="primary" appearance="subtle" size="xs">
                Overridden here ({row.scopeOverrideLanguages.map(languageLabel).join(', ')})
            </Badge>
        );
    }
    if (row.profileOverrideLanguages?.length) {
        return (
            <Badge variant="info" appearance="subtle" size="xs">
                From seller profile ({row.profileOverrideLanguages.map(languageLabel).join(', ')})
            </Badge>
        );
    }
    return 'Global';
}

/**
 * All content template names. The endpoint returns the full list (a few dozen rows), so searching and
 * sorting happen here rather than round-tripping. With a scope in the bar, the global override count
 * makes way for how each template stands at that scope, customized templates first.
 */
const EmailContentsTable: React.FC<EmailContentsTableProps> = ({ data, isLoading }) => {
    const [tableState, setTableState] = useState(DataTable.getInitialState('name'));
    const location = useLocation();
    const [scope] = useScopeInUrl();
    const hasScope = scope.projectId != null || scope.profileId != null;

    const rows = useMemo(() => {
        if (!data) {
            return [];
        }

        const search = tableState.searchText.trim().toLowerCase();
        const filtered = search ? data.filter((x) => x.name.toLowerCase().includes(search)) : data;

        const direction = tableState.sortDirection === SortDirection.Ascending ? 1 : -1;
        return [...filtered].sort((a, b) => {
            // Scoped view groups by relevance to the scope; the chosen column sorts within the groups.
            if (hasScope) {
                const byStatus = statusRank(a) - statusRank(b);
                if (byStatus !== 0) {
                    return byStatus;
                }
            }
            const left = a[tableState.sortBy];
            const right = b[tableState.sortBy];
            if (typeof left === 'number' && typeof right === 'number') {
                return (left - right) * direction;
            }
            return String(left).localeCompare(String(right)) * direction;
        });
    }, [data, tableState, hasScope]);

    if (!data) {
        return <LoadingIndicator />;
    }

    return (
        <DataTable
            hasSearchField
            hasPagination={false}
            isLoading={isLoading}
            data={rows}
            state={tableState}
            onStateChange={setTableState}
            className="table style-3">
            <DataTable.Column
                header="Template"
                field="name"
                sortable
                renderer={({ value }) => (
                    // The scope rides along, so the editor opens focused on the same project/profile.
                    <CustomLink to={`/email-templates/contents/${encodeURIComponent(value)}${location.search}`}>
                        {value}
                    </CustomLink>
                )}
            />
            <DataTable.Column
                header="Languages"
                field="languages"
                renderer={({ value }) => (value.length ? value.join(', ') : <em>Any</em>)}
            />
            <DataTable.Column
                header="Sends to"
                field="sendsToBuyer"
                renderer={({ row }) => {
                    const recipients = [row.sendsToBuyer && 'Buyer', row.sendsToSeller && 'Seller'].filter(Boolean);
                    return recipients.length ? recipients.join(', ') : '—';
                }}
            />
            {hasScope ? (
                <DataTable.Column
                    header={scope.projectId != null ? 'This project' : 'This profile'}
                    field="scopeOverrideLanguages"
                    renderer={({ row }) => renderStatus(row)}
                />
            ) : (
                <DataTable.Column
                    header="Overrides"
                    field="overrideCount"
                    sortable
                    renderer={({ value }) =>
                        value > 0 ? (
                            <Badge variant="info" appearance="subtle" size="xs">
                                {value}
                            </Badge>
                        ) : (
                            '—'
                        )
                    }
                />
            )}
        </DataTable>
    );
};

export default EmailContentsTable;
